import React, { useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import OntologyBrowser, { BrowserSource, OntologyColumn, OntologyFilter } from '../Ontology/OntologyBrowser';
import { TierPill } from './BenchmarkPrimitives';
import {
    TIERS_BY_URGENCY, outcomeColor, outcomeLabel, signalPhrase, tierLabel, scenarioName
} from './BenchmarkVocabulary';
import { ScoredAlert, benchmarkService } from '../../services/benchmarkService';
import { dsVar, ink } from '../../theme/tokens';

const PAGE_SIZE = 15;
/** Hard ceiling on an export, so a large run cannot hang the browser. */
const EXPORT_MAX_ROWS = 5000;

/** RFC 4180 quoting. Without it a typology or reason containing a comma splits the row. */
const csvCell = (value: unknown): string => {
    const text = value === null || value === undefined ? '' : String(value);
    return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const CSV_COLUMNS: { header: string; value: (a: ScoredAlert) => unknown }[] = [
    { header: 'Alert ID', value: (a) => a.alertId },
    { header: 'Customer', value: (a) => a.customerRef },
    { header: 'Scenario', value: (a) => scenarioName(a.typology) },
    { header: 'Scenario code', value: (a) => a.typology },
    { header: 'Period', value: (a) => a.configEpoch },
    { header: 'Priority now', value: (a) => tierLabel(a.incumbentTier) },
    { header: 'Priority with agent', value: (a) => tierLabel(a.agentTier) },
    { header: 'Outcome', value: (a) => outcomeLabel(a.outcomeClass) },
    { header: 'SAR filed', value: (a) => (a.sarFiled ? 'Yes' : 'No') },
    { header: 'Raised by agent', value: (a) => (a.isRescued ? 'Yes' : 'No') },
    {
        header: 'Why it was raised',
        value: (a) => (a.elevatingSignals || []).map((s) => signalPhrase(s.code, s.value)).join('; ')
    }
];

interface RescuedRisksTableProps {
    runKey: number;
    /** Query-string filters, owned by the page so a filtered view is linkable. */
    filters: Record<string, string>;
    onRowSelect: (alert: ScoredAlert) => void;
    selectedAlertId?: string | null;
    /** Scenario codes present in this run — the filter options come from the data. */
    typologyCodes: string[];
}

/**
 * The alerts themselves: what your current rules would have deprioritised.
 *
 * Rebuilt on OntologyBrowser rather than the bespoke MUI table it used to be, so
 * it inherits the app's paging, URL-backed filters, dim-in-place refresh, row
 * selection and error copy instead of maintaining a second, divergent set.
 */
const RescuedRisksTable: React.FC<RescuedRisksTableProps> = ({
    runKey, filters, onRowSelect, selectedAlertId, typologyCodes
}) => {
    const [exporting, setExporting] = useState(false);

    const source: BrowserSource = useMemo(() => ({
        list: (params) => benchmarkService.getScoredAlerts(runKey, {
            page: Number(params.page) || 1,
            page_size: PAGE_SIZE,
            // Defaults to the rescued view: it is the reason this table exists.
            // An explicit "all" turns the narrowing off.
            filter_rescued_only: params.view === 'all' ? undefined : true,
            sar_only: params.sar === 'yes' ? true : undefined,
            segment_key: params.scenario || undefined,
            predicted_tier: params.agent_tier || undefined
        }),
        normalize: (res) => ({
            documents: (res?.alerts ?? []) as Record<string, any>[],
            pagination: {
                page: res?.pagination?.currentPage ?? 1,
                limit: res?.pagination?.pageSize ?? PAGE_SIZE,
                total: res?.pagination?.totalRecords ?? 0,
                pages: res?.pagination?.totalPages ?? 1
            }
        }),
        codeOf: (doc) => (doc.alertId ? String(doc.alertId) : null),
        detailBase: '',
        label: 'alerts'
    }), [runKey]);

    const columns: OntologyColumn[] = useMemo(() => [
        {
            key: 'alertId',
            label: 'Alert',
            width: '16%',
            render: (doc) => (
                <Box>
                    <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8125rem', color: dsVar('fg-body') }}>
                        {doc.alertId}
                    </Typography>
                    <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem', color: dsVar('fg-muted') }}>
                        {doc.customerRef}
                    </Typography>
                </Box>
            )
        },
        {
            key: 'typology',
            label: 'Scenario',
            width: '18%',
            render: (doc) => (
                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft') }}>
                    {scenarioName(doc.typology)}
                </Typography>
            )
        },
        {
            key: 'movement',
            label: 'Priority change',
            width: '22%',
            render: (doc) => (
                <Box className="flex items-center gap-1.5 flex-wrap">
                    <TierPill code={doc.incumbentTier} showCode={false} />
                    <Typography component="span" sx={{ color: dsVar('fg-muted'), fontSize: '0.8125rem' }}>→</Typography>
                    <TierPill code={doc.agentTier} showCode={false} />
                </Box>
            )
        },
        {
            key: 'outcome',
            label: 'What investigators found',
            width: '16%',
            render: (doc) => (
                <Box>
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: ink(outcomeColor(doc.outcomeClass)) }}>
                        {outcomeLabel(doc.outcomeClass)}
                    </Typography>
                    {doc.sarFiled && (
                        <Typography sx={{ fontSize: '0.6875rem', color: ink('#FBBF24'), fontWeight: 600 }}>
                            SAR filed
                        </Typography>
                    )}
                </Box>
            )
        },
        {
            key: 'reason',
            label: 'Why the agent raised it',
            width: '28%',
            render: (doc) => {
                const signals = (doc.elevatingSignals ?? []) as ScoredAlert['elevatingSignals'];
                if (signals.length === 0) {
                    return (
                        <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted') }}>
                            No single signal stood out
                        </Typography>
                    );
                }
                return (
                    <Box>
                        <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft'), lineHeight: 1.5 }}>
                            {signalPhrase(signals[0].code, signals[0].value)}
                        </Typography>
                        {signals.length > 1 && (
                            <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), mt: 0.25 }}>
                                +{signals.length - 1} more
                            </Typography>
                        )}
                    </Box>
                );
            }
        }
    ], []);

    const filterControls: OntologyFilter[] = useMemo(() => [
        {
            key: 'view',
            label: 'Show',
            options: [
                { value: '', label: 'Only alerts the agent raised' },
                { value: 'all', label: 'All scored alerts' }
            ]
        },
        {
            key: 'sar',
            label: 'SAR',
            options: [
                { value: '', label: 'SAR filed or not' },
                { value: 'yes', label: 'SAR filed only' }
            ]
        },
        {
            // Options come from the run rather than a hardcoded list of four
            // codes, which left the dropdown empty for any other scenario set.
            key: 'scenario',
            label: 'Scenario',
            options: [
                { value: '', label: 'All scenarios' },
                ...typologyCodes.map((code) => ({ value: code, label: scenarioName(code) }))
            ]
        },
        {
            key: 'agent_tier',
            label: 'Agent priority',
            options: [
                { value: '', label: 'Any priority' },
                ...TIERS_BY_URGENCY.map((code) => ({ value: code, label: tierLabel(code) }))
            ]
        }
    ], [typologyCodes]);

    /**
     * Exports every row matching the current filters.
     *
     * The old export wrote only the 15 rows on screen while being labelled an
     * audit export, and did not escape its fields.
     */
    const exportCsv = async () => {
        setExporting(true);
        try {
            const rows: ScoredAlert[] = [];
            let page = 1;
            let totalPages = 1;

            do {
                const res = await benchmarkService.getScoredAlerts(runKey, {
                    page,
                    page_size: 100,
                    filter_rescued_only: filters.view === 'all' ? undefined : true,
                    sar_only: filters.sar === 'yes' ? true : undefined,
                    segment_key: filters.scenario || undefined,
                    predicted_tier: filters.agent_tier || undefined
                });
                rows.push(...(res?.alerts ?? []));
                totalPages = res?.pagination?.totalPages ?? 1;
                page += 1;
            } while (page <= totalPages && rows.length < EXPORT_MAX_ROWS);

            const csv = [
                CSV_COLUMNS.map((c) => csvCell(c.header)).join(','),
                ...rows.map((alert) => CSV_COLUMNS.map((c) => csvCell(c.value(alert))).join(','))
            ].join('\n');

            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `benchmark-alerts-run-${runKey}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } finally {
            setExporting(false);
        }
    };

    return (
        <OntologyBrowser
            source={source}
            filters={filters}
            columns={columns}
            filterControls={filterControls}
            // The alerts endpoint has no search parameter, so no search box.
            emptyMessage="No alerts match these filters for this test run."
            onRowSelect={(doc) => onRowSelect(doc as unknown as ScoredAlert)}
            selectedCode={selectedAlertId ?? null}
            showActivityFilter={false}
            toolbar={
                <Box className="flex justify-end mb-2">
                    <Button
                        onClick={exportCsv}
                        disabled={exporting}
                        size="small"
                        startIcon={<DownloadOutlinedIcon sx={{ fontSize: 16 }} />}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.8125rem',
                            fontWeight: 600,
                            color: dsVar('fg-soft'),
                            border: `1px solid ${dsVar('line')}`,
                            borderRadius: '8px',
                            px: 1.5,
                            '&:hover': { borderColor: dsVar('accent-fg'), backgroundColor: 'rgba(255, 222, 89, 0.08)' },
                            '&.Mui-disabled': { color: dsVar('fg-faint'), borderColor: dsVar('line') }
                        }}
                    >
                        {exporting ? 'Preparing export…' : 'Export these alerts (CSV)'}
                    </Button>
                </Box>
            }
        />
    );
};

export default RescuedRisksTable;
